import type { ProductLabel, ReleaseEntry } from '../types/changelog'

/**
 * PLACEHOLDER RELEASE NOTES — sample content for design review only.
 * Replace with real release notes before publishing.
 */

export const productFilters: ('All' | ProductLabel)[] = [
  'All',
  'Point of Sale',
  'Inventory',
  'Purchases',
  'Expenses',
  'Customers',
  'Reports & Insights',
  'Business Financing',
]

export const releases: ReleaseEntry[] = [
  {
    id: 'september-early-access',
    month: 'September 2026',
    date: '22 September 2026',
    tag: 'New',
    products: ['Point of Sale', 'Inventory', 'Purchases', 'Reports & Insights'],
    title: 'September Early Access',
    description:
      'Our biggest release of the year is rolling out to Early Access businesses. It brings faster checkout, smarter stock alerts, and a clearer view across every branch — shaped by feedback from shop owners across Kigali.',
    highlights: [
      'Low-stock alerts sent by SMS, even when you’re away from the shop',
      'Consolidated reports that combine every branch in one view',
      'EBM receipts print up to 3× faster at busy counters',
      'Supplier balances and due dates on one screen',
    ],
    featured: true,
    visual: {
      kind: 'devices',
      tablet: {
        module: 'Reports',
        greeting: 'All branches · September',
        kpis: [
          { label: 'Revenue', value: 'RWF 12.8M', delta: { label: '+7.2%', tone: 'positive' } },
          { label: 'Branches', value: '3' },
          { label: 'Low stock', value: '6 items', delta: { label: 'SMS sent', tone: 'brand' } },
        ],
        main: {
          type: 'progress',
          title: 'Revenue by branch',
          rows: [
            { label: 'Remera', value: 'RWF 5.6M', pct: 100 },
            { label: 'Kimironko', value: 'RWF 4.2M', pct: 75 },
            { label: 'Nyamirambo', value: 'RWF 3.0M', pct: 54 },
          ],
        },
        side: {
          type: 'list',
          title: 'Suppliers due',
          rows: [
            { label: 'Kivu Distributors', value: 'RWF 1.2M', tone: 'warning' },
            { label: 'Huye Dairy Co-op', value: 'Settled', tone: 'positive' },
          ],
        },
      },
      phone: {
        type: 'list',
        title: 'SMS · Low stock',
        badge: { label: 'Now', tone: 'brand' },
        rows: [
          { label: 'Maize flour 5kg', sublabel: 'Remera', value: '3 left', tone: 'negative' },
          { label: 'Cooking oil 1L', sublabel: 'Kimironko', value: '6 left', tone: 'warning' },
        ],
        action: 'Reorder',
      },
    },
  },
  {
    id: 'low-stock-sms',
    month: 'September 2026',
    date: '22 September 2026',
    tag: 'New',
    products: ['Inventory'],
    title: 'Low-stock alerts by SMS',
    description:
      'Get a text message the moment a product drops below its reorder level, so you can restock before customers notice. Choose which staff receive alerts for each branch.',
    visual: {
      kind: 'card',
      card: {
        type: 'progress',
        title: 'Alert settings · Remera',
        badge: { label: 'SMS on', tone: 'positive' },
        rows: [
          { label: 'Maize flour 5kg', value: 'Alert at 5', pct: 25, tone: 'warning' },
          { label: 'Cooking oil 1L', value: 'Alert at 10', pct: 40 },
          { label: 'Rice 5kg', value: 'Alert at 12', pct: 60 },
        ],
      },
    },
  },
  {
    id: 'branch-reports',
    month: 'August 2026',
    date: '26 August 2026',
    tag: 'New',
    products: ['Reports & Insights'],
    title: 'Consolidated branch reports',
    description:
      'See sales, profit, and stock for every branch in a single report, or compare them side by side. Owners with several shops no longer need to add up numbers by hand at month end.',
    visual: {
      kind: 'card',
      card: {
        type: 'bars',
        title: 'All branches · Weekly revenue',
        value: 'RWF 3.4M',
        delta: { label: '+12%', tone: 'positive' },
        bars: [
          { label: 'W1', value: 2.6 },
          { label: 'W2', value: 2.9 },
          { label: 'W3', value: 3.0 },
          { label: 'W4', value: 3.4, highlight: true },
        ],
        legend: 'Remera 44% · Kimironko 33% · Nyamirambo 23%',
      },
    },
  },
  {
    id: 'faster-ebm',
    month: 'August 2026',
    date: '12 August 2026',
    tag: 'Improved',
    products: ['Point of Sale'],
    title: 'Faster EBM receipt printing',
    description:
      'Receipts now print up to three times faster on supported printers, even on slower connections. Queues move quicker during the morning and evening rush.',
  },
  {
    id: 'supplier-balances',
    month: 'July 2026',
    date: '29 July 2026',
    tag: 'New',
    products: ['Purchases'],
    title: 'Supplier balances',
    description:
      'Track what you owe each supplier, with due dates and payment history in one place. Record payments by mobile money or bank transfer and balances update instantly.',
    visual: {
      kind: 'card',
      card: {
        type: 'list',
        title: 'Supplier balances',
        rows: [
          { label: 'Kivu Distributors', sublabel: 'Due 20 Sep', value: 'RWF 1,240,000', tone: 'warning' },
          { label: 'Nyabugogo Wholesale', sublabel: 'Due 28 Sep', value: 'RWF 684,500' },
          { label: 'Huye Dairy Co-op', sublabel: 'Paid 15 Sep', value: 'Settled', tone: 'positive' },
        ],
        footer: { label: 'Total owed', value: 'RWF 1,924,500' },
      },
    },
  },
  {
    id: 'receipt-photo-fix',
    month: 'July 2026',
    date: '8 July 2026',
    tag: 'Fixed',
    products: ['Expenses'],
    title: 'Receipt photos upload reliably offline',
    description:
      'Fixed an issue where receipt photos taken without a connection could fail to attach. Photos are now saved on your device and upload automatically when you’re back online.',
  },
  {
    id: 'customer-statements',
    month: 'June 2026',
    date: '24 June 2026',
    tag: 'New',
    products: ['Customers'],
    title: 'Customer statements by SMS',
    description:
      'Send a customer their balance and recent purchases by SMS with one tap. Each message includes a link to a full statement they can open on any phone.',
    older: true,
  },
  {
    id: 'finance-checklist',
    month: 'June 2026',
    date: '16 June 2026',
    tag: 'Improved',
    products: ['Business Financing'],
    title: 'Clearer business record checklist',
    description:
      'The Business Financing checklist now shows exactly which records are complete and what to add next, so you know how to strengthen your business profile.',
    older: true,
  },
  {
    id: 'split-payment-fix',
    month: 'June 2026',
    date: '5 June 2026',
    tag: 'Fixed',
    products: ['Point of Sale'],
    title: 'Split payments total correctly on refunds',
    description:
      'Fixed an issue where refunding a sale paid partly by mobile money and partly by cash could show the wrong amount per method in the daily cash-up.',
    older: true,
  },
]
